import type { ComponentPublicInstance } from "vue";
import { computed, createApp, markRaw, shallowRef } from "vue";

import { appInstance } from "shared/boot/app";
import { getI18n } from "shared/boot/i18n";
import ModalsContainer from "shared/components/core/modals/ModalsContainer.vue";
import type { Nullable } from "shared/types";

export interface ModalServiceOptions {
  events?: Record<string, Function>;
  noBackdrop?: boolean;
  props?: Record<string, any>;
}

export interface ModalContainerOptions<T> extends ModalServiceOptions {
  component: T;
  name: string;
}

type ModalsContainerInstance = InstanceType<typeof ModalsContainer>;

interface RegisteredModals<T> {
  [key: string]: T;
}

const registeredModals: RegisteredModals<ComponentPublicInstance> = {};
const modalsContainerRef = shallowRef<Nullable<ModalsContainerInstance>>(null);

export const modalCount = computed<number>(
  () => modalsContainerRef.value?.modals.length ?? 0
);

export const hasModalOpen = computed<boolean>(() => modalCount.value > 0);

function setupModalsContainer() {
  if (modalsContainerRef.value) return;

  const modalsApp = createApp(ModalsContainer);
  modalsApp.use(getI18n());

  // Need to copy the context from the main app to keep installed plugins, components, config etc.
  // Isn't much of an option here because quasar hides our app instance creation from us
  // so can't use a factory pattern to reuse options when creating new app instances

  /* eslint-disable no-underscore-dangle */
  Object.assign(modalsApp._context, appInstance._context, {
    app: modalsApp._context.app,
    config: {
      ...modalsApp._context.config,
      ...appInstance._context.config,
    },
  });
  /* eslint-enable no-underscore-dangle */

  const div = document.createElement("div");
  document.body.appendChild(div);
  modalsContainerRef.value = modalsApp.mount(div) as ModalsContainerInstance;
}

export const ModalService = {
  close(modalName: string) {
    modalsContainerRef.value?.closeModal(modalName);
  },
  closeAll() {
    modalsContainerRef.value?.closeAllModals();
  },
  hasModalOpen(): boolean {
    return hasModalOpen.value;
  },
  open(component: string, options: ModalServiceOptions = {}) {
    setupModalsContainer();

    const modal = registeredModals[component];

    const modalOptions: ModalContainerOptions<typeof modal> = {
      component: markRaw(modal),
      events: {
        ...options.events,
        close: () => {
          if (options.events?.close instanceof Function) options.events.close();
          modalsContainerRef.value!.closeModal(component);
        },
      },
      name: component,
      noBackdrop: options.noBackdrop,
      props: options.props,
    };

    modalsContainerRef.value!.openModal(modalOptions);
  },
  registerModals<T>(modals: RegisteredModals<T>) {
    Object.assign(registeredModals, modals);
  },
};

export default ModalService;
